import React, { useEffect } from 'react';

import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { LoadingStatus } from 'src/constants/loading-status';
import { setSelectedProject } from 'src/redux/jira/actions';
import {
  IssueType,
  Project,
  ProjectAssociation,
  ProjectId,
  Site,
} from 'src/redux/jira/types';
import { BucketState } from 'src/types/state';

import { CreateJiraIssueInput } from './create-jira-issue-input';
import * as styles from './create-jira-issue-pr.styled';
import { renderIssueMetaError } from './create-jira-issue-render-error';
import messages from './create-jira-issue.i18n';
import { ProjectChooserJiraTab } from './project-chooser-jira-tab';
import { useIssueCreation } from './use-issue-creation';
import { useIssueMeta } from './use-issue-meta';
import { useIssueSummary } from './use-issue-summary';

export const createInJiraLink = (
  site: Site | undefined,
  project: Project | undefined,
  issueType: IssueType | undefined
) => {
  if (!site || !project || !issueType) {
    return null;
  }

  return (
    <a
      target="_blank"
      href={`${site.cloudUrl}/secure/CreateIssue.jspa?pid=${project.id}&issuetype=${issueType.id}`}
    >
      <FormattedMessage {...messages.createInJiraLinkText} />
    </a>
  );
};

type Props = {
  closeDialog: () => void;
};

export const CreateJiraIssueJiraTab = ({ closeDialog }: Props) => {
  const dispatch = useDispatch();
  const { issueSummary, handleIssueSummaryChange } = useIssueSummary();

  const {
    projectAssociations,
    projectAssociationsFetchedStatus,
    selectedProjectId,
    selectedProjectFetchedStatus,
  } = useSelector<
    BucketState,
    {
      projectAssociations: ProjectAssociation[];
      projectAssociationsFetchedStatus: LoadingStatus;
      selectedProjectId: ProjectId | undefined;
      selectedProjectFetchedStatus: LoadingStatus;
    }
  >(state => ({
    projectAssociations: state.jira.jiraRepoPage.projectAssociations,
    projectAssociationsFetchedStatus:
      state.jira.jiraRepoPage.relevantProjectsFetchedStatus,
    selectedProjectId: state.jira.createJiraIssue.preferences.value,
    selectedProjectFetchedStatus:
      state.jira.createJiraIssue.preferences.fetchedStatus,
  }));

  const selectedProjectMaybe = selectedProjectId
    ? projectAssociations.find(
        association =>
          association.project.site.cloudId === selectedProjectId.cloudId &&
          association.project.id === selectedProjectId.projectId
      )
    : undefined;
  const selectedProject =
    selectedProjectMaybe?.project ||
    (projectAssociations.length > 0
      ? projectAssociations[0].project
      : undefined);

  const { issueMeta, selectedIssueType, handleSetSelectedIssueType } =
    useIssueMeta(selectedProject);

  const {
    issueCreationFailureReason,
    createJiraTabJiraIssue,
    isCreatingIssue,
    isInvalid,
  } = useIssueCreation(selectedProject, selectedIssueType, issueSummary);

  const isProjectChooserDisabled =
    isCreatingIssue ||
    projectAssociationsFetchedStatus === LoadingStatus.Forbidden ||
    projectAssociationsFetchedStatus === LoadingStatus.Failed;
  const isProjectChooserLoading =
    selectedProjectFetchedStatus === LoadingStatus.Before ||
    selectedProjectFetchedStatus === LoadingStatus.Fetching ||
    projectAssociationsFetchedStatus === LoadingStatus.Before ||
    projectAssociationsFetchedStatus === LoadingStatus.Fetching;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createJiraTabJiraIssue();
  };

  useEffect(() => {
    if (!selectedProjectId && selectedProject) {
      dispatch(
        // Set the selected project id when preferences are un-set but the first project
        // in the list has been selected as a default project.
        setSelectedProject({
          cloudId: selectedProject.site.cloudId,
          projectId: selectedProject.id,
        })
      );
    }
  }, [selectedProject, selectedProjectId, dispatch]);

  return (
    <React.Fragment>
      <styles.CreateJiraIssueWrapper
        onSubmit={handleSubmit}
        data-testid="jira-tab-create-jira-issue-form"
      >
        <CreateJiraIssueInput
          issueSummary={issueSummary}
          handleIssueSummaryChange={handleIssueSummaryChange}
          selectedIssueType={selectedIssueType}
          handleSetSelectedIssueType={handleSetSelectedIssueType}
          isCreatingIssue={isCreatingIssue}
          isInvalid={isInvalid}
          issueMeta={issueMeta}
        />
        {renderIssueMetaError(
          issueMeta.fetchedStatus,
          issueCreationFailureReason,
          selectedProject,
          selectedIssueType
        )}
        <styles.CreateJiraIssueActionsContainer>
          <styles.CreateJiraIssueActionsInnerContainer>
            <FormattedMessage {...messages.siteChooserLabel} />
            <ProjectChooserJiraTab
              isLoading={isProjectChooserLoading}
              isDisabled={isProjectChooserDisabled}
              onSelectedChange={project =>
                dispatch(
                  setSelectedProject({
                    cloudId: project.site.cloudId,
                    projectId: project.id,
                  })
                )
              }
              projects={
                projectAssociations.map(association => association.project) ||
                []
              }
              selected={selectedProject}
            />
          </styles.CreateJiraIssueActionsInnerContainer>
          <div>
            <styles.CancelButton
              appearance="subtle-link"
              isDisabled={isCreatingIssue}
              onClick={() => closeDialog()}
            >
              <FormattedMessage {...messages.cancelButtonText} />
            </styles.CancelButton>
            <styles.SubmitButton
              appearance="subtle-link"
              isDisabled={isCreatingIssue}
              onClick={handleSubmit}
            >
              <FormattedMessage {...messages.submitButtonText} />
            </styles.SubmitButton>
          </div>
        </styles.CreateJiraIssueActionsContainer>
      </styles.CreateJiraIssueWrapper>
    </React.Fragment>
  );
};
